import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton
} from '@mui/material';
import { Add, Close, Delete, Security } from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPermissions,
  createPermission,
  deletePermission,
  clearEditPermission
} from '../../../Redux/Reducers/PermissionReducer';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import usePermissions from '../../../hooks/usePermissions';

const MODULE_KEY = 'permission';

const PermissionManagement = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  // For Thead Width
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLarge = useMediaQuery(theme.breakpoints.up('sm'));

  // Redux State
  const { permissions, loading, pagination } = useSelector((state) => state.PermissionReducer);

  const [open, setOpen] = useState(false);
  const [moduleName, setModuleName] = useState('');
  const [actionName, setActionName] = useState('');
  const [actions, setActions] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Pagination Modal State
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const loadPermissions = useCallback(() => {
    dispatch(fetchPermissions({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchText
    }));
  }, [dispatch, paginationModel, searchText]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleAddAction = () => {
    if (!actionName.trim()) return;
    setActions([...actions, actionName.trim()]);
    setActionName('');
  };

  const handleRemoveAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleCreatePermission = async () => {
    if (!moduleName.trim() || actions.length === 0) {
      return toast.warning(t('module_action_required', 'Module name & at least one action required'));
    }
    try {
      await dispatch(createPermission({
        permissions: [{ module: moduleName, actions }]
      })).unwrap();
      toast.success(t('permission_created', 'Permission created successfully'));
      setOpen(false);
      setModuleName('');
      setActions([]);
      loadPermissions();
    } catch (err) {
      toast.error(err || t('permission_create_failed', 'Failed to create permission'));
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deletePermission(deleteId)).unwrap();
      toast.success(t('permission_deleted', 'Permission deleted successfully'));
      setDeleteOpen(false);
      setDeleteId(null);
      loadPermissions();
    } catch (err) {
      toast.error(err || t('permission_delete_failed', 'Failed to delete permission'));
    }
  };

  const columns = [
    {
      field: 'sn',
      headerName: t('sno', 'S.No'),
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const rowsInCurrentPage = params.api.getRowsCount(); 
        const allRows = params.api.getAllRowIds(); 
        const currentRowIndex = allRows.indexOf(params.id);
        const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
        return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
      }
    },
    { 
      field: 'module', 
      headerName: t('module', 'Module'), 
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: 'actions',
      headerName: t('actions', 'Actions'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 2 }),
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.value && params.value.map((action, index) => (
            <Chip key={index} label={action} size="small" variant="outlined" />
          ))}
        </Box>
      )
    },
    {
      field: 'delete',
      headerName: t('delete', 'Delete'),
      width: 100,
      sortable: false,
      renderCell: (params) => !hasAction(MODULE_KEY, 'delete') ? null : (
        <Box sx={{ display: 'flex', gap: '6px', }}>
          <IconButton onClick={() => handleDeleteClick(params.row.module)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
              <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white"/>
              <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white"/>
              <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white"/>
              <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white"/>
              <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white"/>
            </svg>
          </IconButton>
        </Box>
      )
    }
  ];

  const rows = useMemo(() => {
    return permissions.map((item, index) => ({
      rowIndex: index,
      id: item.module, 
      module: item.module,
      actions: Array.isArray(item.permissions)
        ? item.permissions.map(p => p.name)
        : []
    }));
  }, [permissions]);

  return (
    <Box sx={{ p: '36px 24px 24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>
          {t('permission_management_title', 'Permission Management')}
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search_permission', 'Search')}
            sx={{
              '& .MuiOutlinedInput-root': {
                  borderRadius: '999px',
                  backgroundColor: 'rgba(230, 210, 249, 0.2)',
                  color: '#fff',
                  height: '38px',
                  '& fieldset': {
                      borderColor: 'rgba(230, 210, 249, 0.1)',
                  },
                  '&:hover fieldset': {
                      borderColor: 'rgba(230, 210, 249, 0.1)',
                  },
                  '&.Mui-focused fieldset': {
                      borderColor: 'rgba(230, 210, 249, 0.1)',
                      borderWidth: '1px',
                  },
              },
              '& .MuiOutlinedInput-input': {
                  padding: '10px 16px',
                  color: '#FF69B4',
                  fontSize: '15px',
                  lineHeight: '18px',
                  
              },
              '& input::placeholder': {
                  color: 'rgba(255,255,255,0.6)',
                  opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                  ml: '20px'
              }
            }}
          />
          {/* {hasAction(MODULE_KEY, 'create') && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}
          sx={{ 
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
            color: '#fff',
            textTransform: 'none',
            backgroundColor: '#FF69B4 !important',
            borderRadius: '999px',
            fontSize: '15px',
            lineHeight: '24px',
            padding: '0',
            minWidth: '38px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
          ></Button>
          )} */}
        </Box>
      </Box>
      

      <Paper sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={pagination.totalDocs || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
              printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
            },
          }}
          pageSizeOptions={[3, 5, 10, 25]}
          disableRowSelectionOnClick
          localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
          sx={{ 
            backgroundColor: 'transparent', 
            border: 'none', 
            borderRadius: '0', 
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px',
              margin: '0 -12px',
              gap: '10px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-topContainer::after': {
              display: 'none',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
              width: '18px',
              height: '18px',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              maxHeight: 'auto',
              height: 'auto !important'
            },
            '& .MuiDataGrid-virtualScroller': {
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              position: 'inherit',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
              maxHeight: 'none !important',
              height: 'auto !important',
            },
            '& .MuiDataGrid-cell': {
              height: 'auto !important',
              minHeight: 'var(--height)',
              paddingTop: '5px',
              paddingBottom: '5px',
            },
            '& .MuiDataGrid-cell>.MuiBox-root': {
              minHeight: '100%',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
              borderRadius: '0 !important',
              borderColor: 'rgba(230, 210, 249, 0.2) !important',
              color: '#fff',
            },
            '& .MuiDataGrid-columnHeaders [role=row]': {
              backgroundColor: '#1a1a1a',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'rgba(230, 210, 249, 0.2) !important'
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
              color: 'white',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              opacity: '0.8 !important',
              fill: 'white',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined': {
              color: "#fff",
              border: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              padding: '2px',
            },
            '& .MuiTablePagination-root': {
              color: '#fff'
            },
            '& .MuiTablePagination-selectLabel': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-input': {
              marginRight: '10px',
            },
            '& .MuiTablePagination-input .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
            },
            '& .MuiTablePagination-input .MuiSvgIcon-root': {
              fill: 'white'
            },
            '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: '10px !important',
            },
            '& .MuiTablePagination-actions .MuiButtonBase-root': {
              color: '#FF69B4',
            },
            '& .MuiDataGrid-filler': {
              height: 'auto !important',
            },
            '& .MuiDataGrid-filler>div': {
              borderColor: 'transparent',
            },
            '& .MuiDataGrid-overlayWrapper': {
              height: 'auto !important',
              maxHeight: '50px',
            },
            '& .MuiDataGrid-overlay': {
              color: '#fff',
              borderTop: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'transparent',
            },
          }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {t('create_permission', 'Create Permission')}
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers 
          sx={{ 
            borderColor: 'rgba(230, 210, 249, 0.1)', 
            padding: '24px 16px',
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
                background: 'rgba(230, 210, 249, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                background: 'rgba(230, 210, 249, 0.5)',
                },
            },
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <TextField
                fullWidth
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                placeholder={t('module_name', 'Module Name')}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 20px',
                    fontWeight: '400',
                    color: '#fff',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex' }}>
              <Box sx={{ flex: '0 0 calc(100% - 44px)', width: 'calc(100% - 44px)', paddingRight: '10px' }}>
                <TextField
                  fullWidth
                  value={actionName}
                  onChange={(e) => setActionName(e.target.value)}
                  placeholder={t('action_name', 'Action Name')}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      fontWeight: '400',
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '0 0 44px', width: '44px', height: '44px' }}>
                <Button fullWidth variant="contained" onClick={handleAddAction} sx={{ width: '44px', height: '44px', padding: '0', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', minWidth: 'auto' }}>
                  <Add />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ px: '20px', py: '12px' }}>
                {actions.length ? (
                  actions.map((action, index) => (
                    <Chip
                      key={index}
                      label={action}
                      onDelete={() => handleRemoveAction(index)}
                      sx={{ mr: 1, mb: 1, backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', 
                        color: 'white', 
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255,255,255,0.25)'
                        }
                      }}
                    />
                  ))
                ) : (
                  <Typography sx={{ color: 'white', fontSize: '14px', lineHeight: '18px', fontWeight: '400', color: 'rgba(255,255,255,0.6)', }}>{t('no_actions_added', 'No actions added')}</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px',flexWrap: 'wrap', gap: '6px' }}>
          <Button onClick={() => setOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleCreatePermission}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {t('create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t('delete_permission', 'Delete Permission')}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t('confirm_delete_permission', 'Are you sure you want to delete this permission?')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px',flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
          <Button
           variant="contained" 
           onClick={handleConfirmDelete}
           sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <GlobalStyles
        styles={{
          '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
            backgroundColor: '#252525 !important',
            border: '1px solid rgba(230, 210, 249, 0.1)',
            fontSize: '15px',
            color: '#fff',
            borderRadius: '4px',
          },
          '.MuiDialog-root .MuiPaper-root': {
            borderRadius: '20px',
          },
          '.MuiDataGrid-columnsManagementHeader': {
            padding: '10px !important',
          },
          '.MuiInputBase-formControl': {
            fontSize: '14px !important',
            color: 'white !important',
            borderRadius: '999px !important',
          },
          '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
            padding: '4px 8px 4px 16px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
            padding: '0px 16px 12px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
            fontSize: '14px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
            color: '#FF69B4',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
            color: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
            borderColor: 'rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
            display: 'none',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
            padding: '0',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
            fontSize: '14px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
            padding: '5px 10px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: '#FF69B4',
          },
          '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
            borderColor: 'rgba(230, 210, 249, 0.3) !important',
            margin: '0',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            minWidth: '32px',
          },
          '.MuiDataGrid-scrollbarFiller': {
            borderTop: 'none !important',
          },
          '& .MuiDataGrid-scrollbar': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(230, 210, 249, 0.3)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(230, 210, 249, 0.5)',
            },
          },
        }}
      />
    </Box>
  );
};

export default PermissionManagement;


